// Embeds the fonts a set of text boxes use: a subset of each TrueType font as a Type 0 / CIDFontType2 font with Identity-H, so any
// font the person picked (an installed one, or one fetched from a font CDN) shows the same everywhere.

import { subsetTrueType } from "./fontfile";
import { TrueTypeFont } from "./truetype";

export interface EmbedRequest {
    /** Identifies the font program: family, weight, slant. */
    key: string;
    family: string;
    italic: boolean;
    /** A TrueType font (sfnt). */
    sfnt: Uint8Array;
    /** Every string that will be shown in it. */
    texts: string[];
}

export interface EmbeddedFont {
    /** The object number of the Type 0 font. */
    ref: number;
    /** The hex string (2 bytes per character) PDF text operators take for `text`. */
    hex(text: string): string;
}

interface Sink {
    newRef(): number;
    writeObj(num: number, body: string): void;
    writeStream(num: number, dict: string, data: Uint8Array, compress?: boolean): Promise<void>;
}

const h4 = (n: number) => n.toString(16).padStart(4, "0");

/** Writes the font objects for each request; the ones that cannot be embedded (CFF outlines, damaged files) are left out. */
export async function embedFonts(requests: EmbedRequest[], sink: Sink): Promise<Map<string, EmbeddedFont>> {
    const out = new Map<string, EmbeddedFont>();
    for (const r of requests) {
        const font = new TrueTypeFont(r.sfnt);
        if (!font.hasOutlines) continue;
        const gidOf = new Map<number, number>();
        for (const t of r.texts) for (const ch of t) { const cp = ch.codePointAt(0)!; if (!gidOf.has(cp)) gidOf.set(cp, font.gidByUnicode(cp)); }
        const used = new Set<number>([...gidOf.values()].filter((g) => g > 0));
        const sub = subsetTrueType(r.sfnt, used);
        if (!sub) continue;
        const upm = font.unitsPerEm || 1000;
        const scale = (v: number) => Math.round((v * 1000) / upm);
        const gids = [...used].sort((a, b) => a - b);
        const widths = gids.map((g) => { const a = font.advance(g); return `${g} [${a >= 0 ? scale(a) : 600}]`; }).join(" ");
        const bbox = font.headBox.map(scale);
        const { ascent, descent } = font.lineMetrics;
        const psName = `FGSUBS+${r.family.replace(/[^A-Za-z0-9]/g, "")}${r.italic ? "-Italic" : ""}`;

        const fileNum = sink.newRef(), descNum = sink.newRef(), cidNum = sink.newRef(), uniNum = sink.newRef(), type0 = sink.newRef();
        await sink.writeStream(fileNum, `/Length1 ${sub.length}`, sub);
        sink.writeObj(descNum, `<< /Type /FontDescriptor /FontName /${psName} /Flags ${r.italic ? 68 : 4} /FontBBox [${bbox.join(" ")}] /ItalicAngle ${r.italic ? -12 : 0} /Ascent ${scale(ascent)} /Descent ${scale(descent)} /CapHeight ${scale(ascent * 0.7)} /StemV 80 /FontFile2 ${fileNum} 0 R >>`);
        sink.writeObj(cidNum, `<< /Type /Font /Subtype /CIDFontType2 /BaseFont /${psName} /CIDSystemInfo << /Registry (Adobe) /Ordering (Identity) /Supplement 0 >> /FontDescriptor ${descNum} 0 R /DW 1000 /W [${widths}] /CIDToGIDMap /Identity >>`);
        const pairs: string[] = [];
        for (const [cp, g] of gidOf) if (g > 0 && cp < 0x10000) pairs.push(`<${h4(g)}> <${h4(cp)}>`);
        const cmap = `/CIDInit /ProcSet findresource begin\n12 dict begin\nbegincmap\n/CIDSystemInfo << /Registry (Adobe) /Ordering (UCS) /Supplement 0 >> def\n/CMapName /Adobe-Identity-UCS def\n/CMapType 2 def\n1 begincodespacerange\n<0000> <FFFF>\nendcodespacerange\n${pairs.length} beginbfchar\n${pairs.join("\n")}\nendbfchar\nendcmap\nCMapName currentdict /CMap defineresource pop\nend\nend`;
        await sink.writeStream(uniNum, "", new TextEncoder().encode(cmap));
        sink.writeObj(type0, `<< /Type /Font /Subtype /Type0 /BaseFont /${psName} /Encoding /Identity-H /DescendantFonts [${cidNum} 0 R] /ToUnicode ${uniNum} 0 R >>`);
        out.set(r.key, {
            ref: type0,
            hex: (text) => { let s = "<"; for (const ch of text) s += h4(gidOf.get(ch.codePointAt(0)!) ?? 0); return s + ">"; }
        });
    }
    return out;
}
